import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useAuth } from '@src/context';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import { fetchForecast, fetchForecastWhatIf } from '@src/api/actions/dashboard/forecast';
import { PortfolioBanner, ScenarioSummary, SegmentTable } from './components/forecast/ForecastWidgets';

const WHATIF_DEBOUNCE_MS = 300;

export default function ForecastPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useAuth();
  const user = state?.user;
  const isStaff = Boolean(user?.is_staff || user?.is_superuser);

  const clientId = searchParams.get('client_id') || '';

  const {
    options: clientOptions,
    loading: clientLoading,
    handleInputChange: handleClientSearch,
    findOptionByValue,
    fetchClients,
  } = useClientAutocomplete({});
  const [selectedClientOption, setSelectedClientOption] = useState(null);

  // Pre-load the client list so the picker isn't empty before the user types.
  useEffect(() => {
    if (isStaff && fetchClients) fetchClients('');
  }, [isStaff, fetchClients]);

  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [budgets, setBudgets] = useState({}); // budget_id -> monthly $
  const [whatif, setWhatif] = useState(null);
  const [whatifLoading, setWhatifLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const timerRef = useRef(null);

  // Reflect a deep-linked client_id back into the picker label once options load.
  useEffect(() => {
    if (clientId && !selectedClientOption && findOptionByValue) {
      const opt = findOptionByValue(clientId);
      if (opt) setSelectedClientOption(opt);
    }
  }, [clientId, selectedClientOption, findOptionByValue, clientOptions]);

  // ---- load the baseline forecast ---------------------------------------
  useEffect(() => {
    if (!clientId) {
      setForecast(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    setWhatif(null);
    fetchForecast({ client_id: clientId })
      .then((resp) => {
        if (cancelled) return;
        const data = resp?.data || resp;
        setForecast(data);
        const init = {};
        (data?.segments || []).forEach((s) => {
          init[s.budget_id] = s.base_budget;
        });
        setBudgets(init);
        // Auto-expand the top-ranked segment so there's detail in view.
        const firstId = data?.recommendations?.[0]?.budget_id ?? data?.segments?.[0]?.budget_id;
        setExpanded(new Set(firstId != null ? [firstId] : []));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err?.response?.status === 403
            ? 'Forecast is not yet enabled for this account.'
            : 'Could not load the forecast for this client.'
        );
        setForecast(null);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // ---- debounced what-if recompute --------------------------------------
  const runWhatIf = useCallback(
    (nextBudgets) => {
      if (!forecast) return;
      const overrides = (forecast.segments || [])
        .filter((s) => Number(nextBudgets[s.budget_id]) !== Number(s.base_budget))
        .map((s) => ({ budget_id: s.budget_id, budget: nextBudgets[s.budget_id] }));
      if (overrides.length === 0) {
        setWhatif(null);
        return;
      }
      setWhatifLoading(true);
      fetchForecastWhatIf({ client_id: clientId, period: forecast.period, overrides })
        .then((resp) => setWhatif(resp?.data || resp))
        .catch(() => {})
        .finally(() => setWhatifLoading(false));
    },
    [forecast, clientId]
  );

  const handleSlider = useCallback(
    (budgetId, value) => {
      setBudgets((prev) => {
        const next = { ...prev, [budgetId]: value };
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => runWhatIf(next), WHATIF_DEBOUNCE_MS);
        return next;
      });
    },
    [runWhatIf]
  );

  const toggleExpand = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleReset = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const init = {};
    (forecast?.segments || []).forEach((s) => {
      init[s.budget_id] = s.base_budget;
    });
    setBudgets(init);
    setWhatif(null);
  }, [forecast]);

  // ---- derived ----------------------------------------------------------
  const displayScenarios = whatif?.scenarios || forecast?.scenarios;

  const outputsById = useMemo(() => {
    const src = whatif?.segments || forecast?.segments || [];
    const map = {};
    src.forEach((s) => {
      map[s.budget_id] = s.outputs;
    });
    return map;
  }, [whatif, forecast]);

  const recById = useMemo(() => {
    const map = {};
    (forecast?.recommendations || []).forEach((r) => {
      map[r.budget_id] = r;
    });
    return map;
  }, [forecast]);

  // Order segments by recommendation rank (exceptions first), then leftovers.
  const orderedSegments = useMemo(() => {
    const segs = forecast?.segments || [];
    const byId = {};
    segs.forEach((s) => {
      byId[s.budget_id] = s;
    });
    const ordered = [];
    (forecast?.recommendations || []).forEach((r) => {
      if (byId[r.budget_id]) {
        ordered.push(byId[r.budget_id]);
        delete byId[r.budget_id];
      }
    });
    Object.values(byId).forEach((s) => ordered.push(s));
    return ordered;
  }, [forecast]);

  const daysInMonth = useMemo(() => {
    if (!forecast?.period) return 30;
    const [y, m] = forecast.period.split('-').map(Number);
    return new Date(y, m, 0).getDate();
  }, [forecast]);

  const periodLabel = forecast?.period
    ? new Date(`${forecast.period}T00:00:00`).toLocaleDateString(undefined, {
        month: 'long',
        year: 'numeric',
      })
    : '';

  const dirty = useMemo(
    () => (forecast?.segments || []).some((s) => Number(budgets[s.budget_id]) !== Number(s.base_budget)),
    [budgets, forecast]
  );

  // ---- render -----------------------------------------------------------
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Forecast
          </Typography>
          {periodLabel && (
            <Typography variant="body2" color="text.secondary">
              Projecting {periodLabel} from recent performance
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {isStaff && (
            <Autocomplete
              size="small"
              sx={{ minWidth: 280 }}
              loading={clientLoading}
              options={clientOptions}
              value={selectedClientOption}
              onChange={(_e, option) => {
                setSelectedClientOption(option || null);
                setSearchParams(option?.value ? { client_id: String(option.value) } : {});
              }}
              onInputChange={handleClientSearch}
              getOptionLabel={(o) => o?.label || ''}
              isOptionEqualToValue={(o, v) => String(o?.value) === String(v?.value)}
              renderOption={(props, option) => (
                <li {...props} key={option?.value ?? option?.label}>{option?.label || ''}</li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Client" placeholder="Search clients…" />
              )}
            />
          )}
          {dirty && (
            <Button size="small" onClick={handleReset} variant="outlined">
              Reset
            </Button>
          )}
        </Stack>
      </Stack>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      {!clientId && !error && (
        <Alert severity="info">Select a client to see their forecast.</Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && forecast && (
        <Stack spacing={3}>
          <Box>
            <ScenarioSummary scenarios={displayScenarios} goalCom={forecast.goal_com} />
            <PortfolioBanner portfolio={forecast.portfolio} goalSource={forecast.goal_source} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Low / expected / high based on this client&apos;s own recent data — actual results vary.
            </Typography>
          </Box>

          <Divider />

          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                By segment
              </Typography>
              {whatifLoading && <CircularProgress size={16} />}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
              Sorted by what needs attention first. Click a segment to see our recommendation, a budget
              slider, and how efficiency changes as you scale.
            </Typography>
            <SegmentTable
              segments={orderedSegments}
              recById={recById}
              outputsById={outputsById}
              budgets={budgets}
              goalCom={forecast.goal_com}
              daysInMonth={daysInMonth}
              expanded={expanded}
              onToggle={toggleExpand}
              onSlider={handleSlider}
            />
          </Box>
        </Stack>
      )}
    </Box>
  );
}
